import React, { useEffect, useMemo, useRef } from 'react'
import { isMobile } from 'lib/device'
import cn from 'classnames'
import useQuestChannel from 'state/channels/useQuestChannel'
import usePanelContext from 'refactor/hooks/usePanelContext'
import useGetQuest from 'refactor/hooks/api/useGetQuest'
import {
    isPanelLeft,
    isPanelCenter,
    isPanelRight,
    isPanelOneOffLeft,
    isPanelOneOffRight,
} from 'state/imperativeApis/swiperApi'
import QuestOrig from 'components/quests/Quest'
const Quest = QuestOrig as any
import styles from 'components/panels/panel.module.scss'
import api from 'api/api'
import queryClient from 'api/queryClient'
import PrivateQuest from 'components/quests/PrivateQuest'
import QuestNotFound from 'components/quests/QuestNotFound'
import { logEv } from 'lib/log'
import { getCachedQuest, getCachedThreadQuest } from 'state/cache'
import useStore from 'state/knovStore'
import type { Quest } from 'types/quests'
import type { PanelState } from 'state/PanelState'
import { idFromKey } from 'utils'
import { resolvePanelDisplayQuest } from './questDisplay'
import { markPanelNavQuestSettled } from 'lib/panelNavMetrics'
import { naturalThreadLayoutEnabled } from 'lib/mediaBuckets'
import { findAnswerTargetElement, isAnswerElementNearTarget } from 'lib/answerTargetScroll'
import { setFirstTouchAuthSurface, QUALIFYING_SURFACES } from 'lib/authSurfaceTracking'
import { findAnswerHighlightElement } from 'lib/answerHighlightTargets'
import { userHasFeature } from 'refactor/hooks/useUserHasFeature'
import { scrollAncestorsEnabled } from 'lib/scrollAncestors'

interface QuestContainerProps {
    filter: {
        questId?: string
        answerId?: string
        query?: string
    }
}

const DEEP_LINK_SCROLL_TIMEOUT_MS = 9000
const DEEP_LINK_HIGHLIGHT_BACKSTOP_POLL_MS = 150
const DEEP_LINK_HIGHLIGHT_TARGET_TOLERANCE_PX = 2
const DEEP_LINK_HIGHLIGHT_STABLE_MS = 220

// Flag-on, the route-owned ensure is the single highlighter, but it can terminate against
// a still-skeleton row (highlight class not yet in the DOM), get cancelled by a stray tap,
// or time out waiting for data. This poll is the delivery guarantee: it terminates on the
// FIRST sighting of the hydrated surface near target, highlighting only if the coordinator's
// own flash isn't already showing — preserving the single-highlight invariant.
function armDeepLinkHighlightBackstop(panel: PanelState, answerId: string) {
    const deadlineAt = Date.now() + DEEP_LINK_SCROLL_TIMEOUT_MS
    let stopped = false
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    let exactSince: number | null = null
    const poll = () => {
        timeoutId = null
        if (stopped || String(panel?.filter?.answerId || '') !== String(answerId)) return

        const scrollContainer = panel.getScrollContainer?.()
        const answerElement = scrollContainer
            ? findAnswerTargetElement(scrollContainer, answerId)
            : null
        const highlightElement = scrollContainer
            ? findAnswerHighlightElement(scrollContainer, answerId)
            : null
        const exactlyPlaced =
            scrollContainer &&
            answerElement &&
            highlightElement &&
            isAnswerElementNearTarget(
                scrollContainer,
                answerElement,
                DEEP_LINK_HIGHLIGHT_TARGET_TOLERANCE_PX,
            )

        if (exactlyPlaced) {
            exactSince ??= Date.now()
        } else {
            exactSince = null
        }
        if (exactlyPlaced && Date.now() - exactSince! >= DEEP_LINK_HIGHLIGHT_STABLE_MS) {
            if (!highlightElement.classList.contains('highlight')) {
                panel.highlightAnswer?.(answerId)
            }
            return
        }
        if (Date.now() < deadlineAt) {
            timeoutId = setTimeout(poll, DEEP_LINK_HIGHLIGHT_BACKSTOP_POLL_MS)
        }
    }
    poll()
    return () => {
        stopped = true
        if (timeoutId) clearTimeout(timeoutId)
    }
}

function isServerBackedViewableQuest(quest: Quest | null | undefined) {
    return (
        !!quest?.id &&
        quest.id !== '__PRIVATE__' &&
        !quest.is_draft &&
        !quest.localChildQuest &&
        !quest.isLocalChildQuest &&
        !quest.isLocalRootQuest &&
        !quest.localPublishErrorStatus
    )
}

function isCurrentQuestRoute(questId: string) {
    const currentPath = window.location?.pathname || ''
    const questPathMatch = currentPath.match(/^\/quest\/([^/]+)(?:\/answer\/[^/]+)?\/?$/)
    const questKey = questPathMatch?.[1]
    if (!questKey) return false

    let decodedQuestKey = questKey
    try {
        decodedQuestKey = decodeURIComponent(questKey)
    } catch (e) {}

    return decodedQuestKey === questId || idFromKey(decodedQuestKey) === questId
}

export default function QuestContainer({ filter }: QuestContainerProps) {
    const {
        panel,
        panelContentActive = true,
        revealPendingOpenPlacement,
        failPendingOpenPlacement,
    } = usePanelContext()
    const panelId = panel?.panelId
    const questId = filter?.questId
    const naturalThreadLayout = naturalThreadLayoutEnabled(filter)
    const { quest, isFetching, isError, error, refetch } = useGetQuest(questId, {
        retainThreadSession: naturalThreadLayout && panelContentActive,
        deferAnswerHydration: naturalThreadLayout && Boolean(filter?.answerId),
        // Threads opened from a stream card render the cached feed payload,
        // which has no ancestors: without this the spine is missing exactly
        // when context matters most (arriving from the feed).
        ensureAncestors: scrollAncestorsEnabled(),
    })
    const cachedQuest = useMemo(() => {
        if (!questId) return null
        if (quest?.id === questId) return quest
        return naturalThreadLayout ? getCachedThreadQuest(questId) : getCachedQuest(questId)
    }, [naturalThreadLayout, questId, quest])
    const displayQuest = resolvePanelDisplayQuest({
        quest,
        cachedQuest,
        isFetching,
        isError,
        questId,
    })
    const hasDisplayQuest = !!displayQuest

    // >>> Hydration issue detection at container level
    if (questId && !isFetching && !isError && !displayQuest) {
        console.warn(
            '>>> QuestContainer: questId provided but quest is null after fetch completed',
            { questId, panelId },
        )
    }

    // Only subscribe to channel if we have a valid quest
    useQuestChannel(isError ? null : questId)

    const newAnswerRef = useRef(null)
    const viewedQuestIdsRef = useRef(new Set<string>())
    const lastPanelNavQuestSettledKeyRef = useRef<string | null>(null)
    const missingTargetRefetchKeyRef = useRef<string | null>(null)

    useEffect(
        function markPanelNavigationQuestSettled() {
            if (!questId || isFetching) return

            const settleState = isError ? 'error' : hasDisplayQuest ? 'display' : 'empty'
            const settledKey = `${panelId || ''}:${questId}:${settleState}`
            if (lastPanelNavQuestSettledKeyRef.current === settledKey) return

            lastPanelNavQuestSettledKeyRef.current = settledKey
            markPanelNavQuestSettled({
                panelId,
                questId,
                isError,
                hasDisplayQuest,
                status:
                    error && typeof error === 'object' && 'status' in error ? error.status : null,
            })
        },
        [panelId, questId, isFetching, isError, hasDisplayQuest, error],
    )

    useEffect(
        function trackViewWhenQuestIsServerBacked() {
            if (!questId || !displayQuest || !isServerBackedViewableQuest(displayQuest)) return
            if (viewedQuestIdsRef.current.has(String(questId))) return

            const isOnScreen = isMobile
                ? isPanelCenter(panelId)
                : isPanelLeft(panelId) || isPanelCenter(panelId) || isPanelRight(panelId)
            const almostOnScreen = isPanelOneOffLeft(panelId) || isPanelOneOffRight(panelId)
            if (!isOnScreen && !almostOnScreen) return

            viewedQuestIdsRef.current.add(String(questId))
            let cancelled = false
            ;(async () => {
                await api.userViewsQuest(questId)
                if (!cancelled)
                    queryClient.invalidateQueries({ queryKey: ['actions'], exact: true })
            })()

            return () => {
                cancelled = true
            }
        },
        [
            questId,
            panelId,
            displayQuest?.id,
            displayQuest?.is_draft,
            displayQuest?.localChildQuest,
            displayQuest?.isLocalChildQuest,
            displayQuest?.isLocalRootQuest,
            displayQuest?.localPublishErrorStatus,
        ],
    )

    useEffect(function onMount() {
        if (!window.gon?.currentUser) {
            setFirstTouchAuthSurface(QUALIFYING_SURFACES.QUEST)
        }

        // UTM params are already attached globally in log.ts defaults
        logEv('QUEST VIEW', { questId })
    }, [])

    useEffect(
        function clearOffRouteMissingRestoredQuest() {
            if (!panelId || !questId || displayQuest || !isError) return
            if (!error || !('status' in error) || error.status !== 404) return

            if (isCurrentQuestRoute(questId)) return

            useStore.getState().panels.clearPanel(panelId)
        },
        [panelId, questId, displayQuest, isError, error],
    )

    useEffect(
        function requestDeepLinkedAnswerReveal() {
            const answerId = filter?.answerId
            if (!panel) return
            if (!answerId) {
                panel.clearEnsuredAnswerReveal?.()
                return
            }
            if (!hasDisplayQuest || !questId) return

            void panel.ensureAnswerReveal?.(answerId, {
                questId,
                deadlineAt: Date.now() + DEEP_LINK_SCROLL_TIMEOUT_MS,
                highlight: true,
            })

            if (!userHasFeature('instant_reply_nav')) return
            return armDeepLinkHighlightBackstop(panel, String(answerId))
        },
        [panel, hasDisplayQuest, questId, filter?.answerId],
    )

    useEffect(
        function releaseTerminalTargetedOpen() {
            const answerId = String(filter?.answerId || '')
            if (!panel || !questId || !answerId || isFetching) return

            const intent = panel.pendingOpenPlacement
            if (
                String(intent?.questId || '') !== String(questId) ||
                String(intent?.answerId || '') !== answerId
            ) {
                return
            }

            const targetIsUnavailable =
                !displayQuest ||
                isError ||
                displayQuest.id === '__PRIVATE__' ||
                !!displayQuest.localPublishErrorStatus
            const targetId = String(answerId)
            const parentId = String(displayQuest?.parent?.id ?? displayQuest?.parent_id ?? '')
            const answerIds = [
                ...(Array.isArray(displayQuest?.sorted_answer_ids)
                    ? displayQuest.sorted_answer_ids
                    : []),
                ...(Array.isArray(displayQuest?.sorted_answers)
                    ? displayQuest.sorted_answers.map(answer => answer?.id)
                    : []),
            ]
                .filter(id => id != null)
                .map(String)
            const fullMembershipExcludesTarget =
                displayQuest?.__knovAnswerHydrationScope === 'full' &&
                parentId !== targetId &&
                !answerIds.includes(targetId)
            if (!targetIsUnavailable && !fullMembershipExcludesTarget) return

            if (intent?.contextScroll) {
                failPendingOpenPlacement?.(
                    questId,
                    targetId,
                    intent,
                    fullMembershipExcludesTarget || displayQuest?.id === '__PRIVATE__'
                        ? 'unavailable'
                        : 'error',
                )
                return
            }
            panel.cancelAnswerReveal?.(questId, targetId)
            panel.consumeOpenPlacement?.(questId, targetId)
            revealPendingOpenPlacement?.(questId, targetId, intent)
        },
        [
            displayQuest,
            filter?.answerId,
            isError,
            isFetching,
            panel,
            questId,
            revealPendingOpenPlacement,
            failPendingOpenPlacement,
        ],
    )

    useEffect(
        function refetchWhenDeepLinkTargetMissing() {
            // A cached quest fresher than the entity staleTime never refetches on mount, so
            // a reply posted after that cache write has no row to place or reveal — the open
            // silently dead-ends. Force one fetch for the missing target.
            if (!userHasFeature('instant_reply_nav')) return
            const answerId = filter?.answerId
            if (!answerId || !questId || !displayQuest || isFetching) return
            if (!isServerBackedViewableQuest(displayQuest)) return

            const targetId = String(answerId)
            const parentId = String(displayQuest.parent?.id ?? displayQuest.parent_id ?? '')
            // The title/parent answer is legitimately absent from the tail answer list.
            if (parentId === targetId) return
            const sortedAnswerIds = Array.isArray(displayQuest.sorted_answer_ids)
                ? displayQuest.sorted_answer_ids
                : []
            const sortedAnswers = Array.isArray(displayQuest.sorted_answers)
                ? displayQuest.sorted_answers
                : []
            const hasTarget =
                sortedAnswerIds.some(id => String(id) === targetId) ||
                sortedAnswers.some(answer => String(answer?.id ?? '') === targetId)
            if (hasTarget) return

            const retryKey = `${questId}:${targetId}`
            if (missingTargetRefetchKeyRef.current === retryKey) return
            missingTargetRefetchKeyRef.current = retryKey
            void refetch?.()
        },
        [displayQuest, filter?.answerId, isFetching, questId, refetch],
    )

    useEffect(
        function cancelDepartedAnswerReveal() {
            const answerId = filter?.answerId
            if (!panel || !questId || !answerId) return

            return () => panel.cancelAnswerReveal?.(questId, answerId)
        },
        [panel, questId, filter?.answerId],
    )

    if (displayQuest?.id === '__PRIVATE__') {
        return <PrivateQuest />
    }

    if (displayQuest?.localPublishErrorStatus) {
        return (
            <QuestNotFound
                error={{
                    status: displayQuest.localPublishErrorStatus,
                    message: displayQuest.localPublishErrorMessage,
                }}
            />
        )
    }

    if (isError && !displayQuest) {
        return <QuestNotFound error={error} />
    }

    return !displayQuest && isFetching ? (
        <Loading />
    ) : displayQuest ? (
        <div className={cn(styles.questContainerComp, isMobile && 'full-bleed-quests')}>
            <Quest
                panelId={panelId}
                panel={panel}
                isActive={true}
                showAll={true}
                quest={displayQuest}
                shouldPlay={window.location.href.endsWith('#play')}
                filter={filter}
                query={filter?.query}
                ix={0}
                newAnswerRef={newAnswerRef}
                naturalThreadLayout={naturalThreadLayout}
            />
        </div>
    ) : null
}

function Loading() {
    return (
        <div className={styles.loader}>
            <div className={cn(styles.loadingIcon)}>
                <i className="fa fa-circle-o-notch fa-spin fa-lg" />
            </div>
        </div>
    )
}
